import {
    getNumberValue,
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import { ShiftInfoCard } from '@apps/web/components/phoebe_chat_view/tool_calls/shift_tool_ui';
import {
    DetailChipList,
    formatContactLine,
    formatDetailStatus,
    formatToolFieldLabel,
    getStringArray,
    joinPresent,
    TOOL_SURFACE_HOVER_CLASS,
    ToolDetailHeader,
    ToolDetailKeyValue,
    ToolToken,
    ToolTopicSection,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { cn } from '@apps/web/lib/utils';

export function joinNameParts(
    firstName: string | null,
    lastName: string | null,
): string | null {
    return (
        [firstName, lastName]
            .filter((part): part is string => part !== null && part.length > 0)
            .join(' ') || null
    );
}

function getCaregiverDetailName(
    caregiver: Record<string, unknown>,
    formatName: (name: string | null | undefined) => string,
): string {
    const personalInfo = isRecord(caregiver['personal_info'])
        ? caregiver['personal_info']
        : null;
    return (
        formatName(
            getStringValue(personalInfo, 'name') ??
                getStringValue(caregiver, 'name'),
        ) || 'Caregiver'
    );
}

function getCaregiverOffice(caregiver: Record<string, unknown>): string | null {
    const administrative = isRecord(caregiver['administrative'])
        ? caregiver['administrative']
        : null;
    return (
        getStringValue(administrative, 'office_name') ??
        getStringValue(caregiver, 'office_name')
    );
}

function getCaregiverRecentShiftStart(
    shift: Record<string, unknown>,
): string | null {
    return (
        getStringValue(shift, 'start') ??
        getStringValue(shift, 'starts_at') ??
        getStringValue(shift, 'shift_start_time') ??
        getStringValue(shift, 'scheduled_start_time') ??
        getStringValue(shift, 'scheduled_start')
    );
}

function getCaregiverRecentShiftEnd(
    shift: Record<string, unknown>,
): string | null {
    return (
        getStringValue(shift, 'end') ??
        getStringValue(shift, 'ends_at') ??
        getStringValue(shift, 'shift_end_time') ??
        getStringValue(shift, 'scheduled_end_time') ??
        getStringValue(shift, 'scheduled_end')
    );
}

function parseOptionalDateMs(value: string | null): number | null {
    if (!value) {
        return null;
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
}

export function formatLocalShortDateTime(value: string | null): string | null {
    const parsedMs = parseOptionalDateMs(value);
    if (parsedMs === null) {
        return null;
    }

    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(new Date(parsedMs));
}

export function formatLocalShortTime(value: string | null): string | null {
    const parsedMs = parseOptionalDateMs(value);
    if (parsedMs === null) {
        return null;
    }

    return new Intl.DateTimeFormat(undefined, {
        hour: 'numeric',
        minute: '2-digit',
    }).format(new Date(parsedMs));
}

function getCaregiverRecentShiftDurationHours(
    shift: Record<string, unknown>,
): number | null {
    const providedHours =
        getNumberValue(shift, 'duration_hours') ??
        getNumberValue(shift, 'scheduled_hours') ??
        getNumberValue(shift, 'hours');
    if (providedHours !== null && providedHours >= 0) {
        return providedHours;
    }

    const startMs = parseOptionalDateMs(getCaregiverRecentShiftStart(shift));
    const endMs = parseOptionalDateMs(getCaregiverRecentShiftEnd(shift));
    if (startMs === null || endMs === null || endMs < startMs) {
        return null;
    }

    return (endMs - startMs) / (60 * 60 * 1000);
}

function formatCaregiverHoursLabel(hours: number): string {
    const rounded = Math.round(hours * 10) / 10;
    const label = Number.isInteger(rounded)
        ? String(rounded)
        : rounded.toFixed(1);
    return `${label} ${rounded === 1 ? 'hour' : 'hours'}`;
}

function formatCaregiverRecentShiftWhen(
    shift: Record<string, unknown>,
): string | null {
    const shiftDay =
        getStringValue(shift, 'shift_day') ?? getStringValue(shift, 'date');
    const startTime = getStringValue(shift, 'start_time');
    const endTime = getStringValue(shift, 'end_time');
    if (shiftDay && startTime && endTime) {
        return `${shiftDay}, ${startTime}-${endTime}`;
    }
    if (shiftDay && startTime) {
        return `${shiftDay}, ${startTime}`;
    }

    const start = getCaregiverRecentShiftStart(shift);
    const end = getCaregiverRecentShiftEnd(shift);
    const formattedStart = formatLocalShortDateTime(start);
    const formattedEnd = formatLocalShortTime(end);
    if (formattedStart && formattedEnd) {
        return `${formattedStart}-${formattedEnd}`;
    }
    return formattedStart;
}

function getCaregiverRecentHoursSummary(
    shifts: ReadonlyArray<Record<string, unknown>>,
): string | null {
    const nowMs = Date.now();
    const sevenDaysAgoMs = nowMs - 7 * 24 * 60 * 60 * 1000;
    const recentHours = shifts.reduce((total, shift) => {
        const startMs = parseOptionalDateMs(
            getCaregiverRecentShiftStart(shift),
        );
        const hours = getCaregiverRecentShiftDurationHours(shift);
        if (startMs === null || hours === null) {
            return total;
        }
        if (startMs < sevenDaysAgoMs || startMs > nowMs) {
            return total;
        }
        return total + hours;
    }, 0);

    return recentHours > 0
        ? `${formatCaregiverHoursLabel(recentHours)} in last 7 days`
        : null;
}

export function CaregiverDetailsCard({
    data,
    onCaregiverClick,
}: {
    readonly data: Record<string, unknown>;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    const { formatName } = useFormatPersonName();
    const caregivers = Array.isArray(data['caregivers'])
        ? data['caregivers'].filter(isRecord)
        : [data].filter(isRecord);
    const warnings = getStringArray(data['warnings']);

    if (caregivers.length === 0) {
        return (
            <p className="text-muted-foreground text-xs">
                No caregiver details returned.
            </p>
        );
    }

    return (
        <div className="space-y-2">
            {caregivers.map((caregiver) => {
                const caregiverId = getStringValue(caregiver, 'caregiver_id');
                const contact = isRecord(caregiver['contact'])
                    ? caregiver['contact']
                    : null;
                const address = isRecord(caregiver['address'])
                    ? caregiver['address']
                    : null;
                const administrative = isRecord(caregiver['administrative'])
                    ? caregiver['administrative']
                    : null;
                const name = getCaregiverDetailName(caregiver, formatName);
                const isActive =
                    caregiver['is_active'] === true ||
                    (isRecord(caregiver['personal_info']) &&
                        caregiver['personal_info']['is_active'] === true);
                const officeName = getCaregiverOffice(caregiver);
                const phone =
                    getStringValue(contact, 'primary_phone') ??
                    getStringValue(caregiver, 'primary_phone');
                const email =
                    getStringValue(contact, 'primary_email') ??
                    getStringValue(caregiver, 'primary_email');
                const cityState = joinPresent([
                    getStringValue(address, 'city'),
                    getStringValue(address, 'state'),
                ]);
                const recentShifts = Array.isArray(caregiver['recent_shifts'])
                    ? caregiver['recent_shifts'].filter(isRecord)
                    : [];
                const recentHoursSummary =
                    getCaregiverRecentHoursSummary(recentShifts);
                const messageHistory = Array.isArray(
                    caregiver['message_history'],
                )
                    ? caregiver['message_history']
                    : [];
                const callHistory = Array.isArray(caregiver['call_history'])
                    ? caregiver['call_history']
                    : [];

                return (
                    <div
                        key={caregiverId ?? name}
                        className={cn(
                            'border-border/60 bg-card rounded-lg border p-3',
                            TOOL_SURFACE_HOVER_CLASS,
                        )}
                    >
                        <ToolDetailHeader
                            title={name}
                            subtitle={officeName ?? cityState}
                            badges={[
                                {
                                    label: isActive ? 'Active' : 'Inactive',
                                    tone: isActive ? 'green' : 'neutral',
                                },
                            ]}
                        />
                        <div className="mt-3 space-y-2">
                            {contact ? (
                                <ToolTopicSection title="Contact">
                                    {formatContactLine(phone, email) ? (
                                        <p className="text-foreground text-xs">
                                            {formatContactLine(phone, email)}
                                        </p>
                                    ) : (
                                        <p className="text-muted-foreground text-xs italic">
                                            No primary contact
                                        </p>
                                    )}
                                </ToolTopicSection>
                            ) : null}
                            <ToolTopicSection title="Roles">
                                <DetailChipList
                                    values={getStringArray(caregiver['roles'])}
                                />
                            </ToolTopicSection>
                            <ToolTopicSection title="Skills">
                                <DetailChipList
                                    values={getStringArray(caregiver['skills'])}
                                />
                            </ToolTopicSection>
                            <ToolTopicSection title="Notes">
                                <DetailChipList
                                    values={[
                                        ...getStringArray(caregiver['tags']),
                                        ...getStringArray(
                                            caregiver['preferences_tags'],
                                        ),
                                    ]}
                                />
                            </ToolTopicSection>
                            {getStringValue(administrative, 'notes') ||
                            getStringValue(
                                administrative,
                                'agent_instructions',
                            ) ? (
                                <ToolTopicSection title="Profile context">
                                    <div className="space-y-1.5">
                                        {getStringValue(
                                            administrative,
                                            'notes',
                                        ) ? (
                                            <p className="text-foreground text-xs">
                                                {getStringValue(
                                                    administrative,
                                                    'notes',
                                                )}
                                            </p>
                                        ) : null}
                                        {getStringValue(
                                            administrative,
                                            'agent_instructions',
                                        ) ? (
                                            <p className="text-muted-foreground text-xs">
                                                {getStringValue(
                                                    administrative,
                                                    'agent_instructions',
                                                )}
                                            </p>
                                        ) : null}
                                    </div>
                                </ToolTopicSection>
                            ) : null}
                            {recentShifts.length > 0 ? (
                                <ToolTopicSection title="Recent shifts">
                                    <div className="space-y-1.5">
                                        {recentHoursSummary ? (
                                            <p className="text-foreground text-xs font-medium">
                                                {recentHoursSummary}
                                            </p>
                                        ) : null}
                                        {recentShifts
                                            .slice(0, 3)
                                            .map((shift, index) => {
                                                const title =
                                                    formatName(
                                                        getStringValue(
                                                            shift,
                                                            'patient_name',
                                                        ) ??
                                                            getStringValue(
                                                                shift,
                                                                'client_name',
                                                            ),
                                                    ) || 'Shift';
                                                const status =
                                                    formatDetailStatus(
                                                        getStringValue(
                                                            shift,
                                                            'status',
                                                        ),
                                                    );
                                                const when =
                                                    formatCaregiverRecentShiftWhen(
                                                        shift,
                                                    );
                                                const durationHours =
                                                    getCaregiverRecentShiftDurationHours(
                                                        shift,
                                                    );

                                                return (
                                                    <div
                                                        key={`caregiver-shift-${index}`}
                                                        className="border-border/50 bg-muted/20 rounded-md border px-2 py-1.5"
                                                    >
                                                        <div className="flex flex-wrap items-center justify-between gap-1.5">
                                                            <p className="text-foreground text-xs font-medium">
                                                                {title}
                                                            </p>
                                                            {status ? (
                                                                <ToolToken tone="blue">
                                                                    {status}
                                                                </ToolToken>
                                                            ) : null}
                                                        </div>
                                                        {when ||
                                                        durationHours !==
                                                            null ? (
                                                            <p className="text-muted-foreground mt-0.5 text-[11px]">
                                                                {joinPresent([
                                                                    when,
                                                                    durationHours !==
                                                                    null
                                                                        ? formatCaregiverHoursLabel(
                                                                              durationHours,
                                                                          )
                                                                        : null,
                                                                ])}
                                                            </p>
                                                        ) : null}
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </ToolTopicSection>
                            ) : null}
                            {messageHistory.length > 0 ||
                            callHistory.length > 0 ? (
                                <ToolTopicSection title="Recent activity">
                                    <p className="text-foreground text-xs">
                                        {messageHistory.length} messages,{' '}
                                        {callHistory.length} calls returned
                                    </p>
                                </ToolTopicSection>
                            ) : null}
                            {caregiverId && onCaregiverClick ? (
                                <button
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        onCaregiverClick(caregiverId);
                                    }}
                                    className="text-primary hover:text-primary/80 cursor-pointer text-xs font-medium"
                                >
                                    Open caregiver profile
                                </button>
                            ) : null}
                        </div>
                    </div>
                );
            })}
            {warnings.map((warning) => (
                <p
                    key={warning}
                    className="text-muted-foreground text-[11px] italic"
                >
                    {warning}
                </p>
            ))}
        </div>
    );
}

export function ClientDetailsCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const { formatName } = useFormatPersonName();
    const firstName = getStringValue(data, 'first_name');
    const lastName = getStringValue(data, 'last_name');
    const name =
        getStringValue(data, 'name') ??
        joinNameParts(firstName, lastName) ??
        'Client';
    const preferredName = getStringValue(data, 'preferred_name');
    const cityState = joinPresent([
        getStringValue(data, 'city'),
        getStringValue(data, 'state'),
    ]);
    const address = joinPresent([
        getStringValue(data, 'address') ??
            getStringValue(data, 'address_line_1'),
        getStringValue(data, 'address_line_2'),
        cityState,
        getStringValue(data, 'postal_code'),
    ]);
    const carePlan =
        getStringValue(data, 'care_plan') ??
        getStringValue(data, 'care_plan_summary');
    const isActive = data['is_active'] === true;

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title={formatName(name)}
                subtitle={preferredName ? `Preferred: ${preferredName}` : null}
                badges={[
                    {
                        label: isActive ? 'Active' : 'Inactive',
                        tone: isActive ? 'green' : 'neutral',
                    },
                ]}
            />
            <ToolTopicSection title="Location">
                <p className="text-foreground text-xs">
                    {address ?? 'No address returned'}
                </p>
            </ToolTopicSection>
            <ToolTopicSection title="Contact">
                <p className="text-foreground text-xs">
                    {formatContactLine(
                        getStringValue(data, 'phone_number'),
                        getStringValue(data, 'email'),
                    ) ?? 'No contact returned'}
                </p>
            </ToolTopicSection>
            {getStringValue(data, 'office_name') ? (
                <ToolTopicSection title="Office">
                    <p className="text-foreground text-xs">
                        {getStringValue(data, 'office_name')}
                    </p>
                </ToolTopicSection>
            ) : null}
            <ToolTopicSection title="Tags">
                <DetailChipList values={getStringArray(data['tags'])} />
            </ToolTopicSection>
            {carePlan ? (
                <ToolTopicSection title="Care plan">
                    <p className="text-foreground text-xs whitespace-pre-wrap">
                        {carePlan}
                    </p>
                </ToolTopicSection>
            ) : null}
        </div>
    );
}

export function ShiftDetailsCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const { formatName } = useFormatPersonName();
    const patient = isRecord(data['patient']) ? data['patient'] : {};
    const timing = isRecord(data['timing']) ? data['timing'] : {};
    const assignment = isRecord(data['assignment']) ? data['assignment'] : {};
    const requirements = isRecord(data['requirements'])
        ? data['requirements']
        : {};
    const notes = isRecord(data['notes']) ? data['notes'] : {};
    const relatedCallouts = Array.isArray(data['related_callouts'])
        ? data['related_callouts'].filter(isRecord)
        : [];
    const patientName = formatName(getStringValue(patient, 'name')) || 'Shift';
    const when = joinPresent([
        getStringValue(timing, 'date'),
        getStringValue(timing, 'start') && getStringValue(timing, 'end')
            ? `${getStringValue(timing, 'start')} - ${getStringValue(
                  timing,
                  'end',
              )}`
            : null,
    ]);
    const status = formatDetailStatus(getStringValue(assignment, 'status'));

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title={patientName}
                subtitle={when}
                badges={status ? [{ label: status, tone: 'blue' }] : []}
            />
            <ToolTopicSection title="Patient">
                <div className="space-y-1.5">
                    <ToolDetailKeyValue
                        label="Address"
                        value={
                            joinPresent([
                                getStringValue(patient, 'address'),
                                getStringValue(patient, 'city'),
                                getStringValue(patient, 'state'),
                            ]) ?? 'Not returned'
                        }
                    />
                    <ToolDetailKeyValue
                        label="Phone"
                        value={
                            getStringValue(patient, 'phone') ?? 'Not returned'
                        }
                    />
                    <ToolDetailKeyValue
                        label="Office"
                        value={
                            getStringValue(patient, 'office_name') ??
                            'Not returned'
                        }
                    />
                </div>
            </ToolTopicSection>
            <ToolTopicSection title="Assignment">
                <div className="space-y-1.5">
                    <ToolDetailKeyValue
                        label="Caregiver"
                        value={
                            formatName(
                                getStringValue(assignment, 'caregiver_name'),
                            ) || 'Unassigned'
                        }
                    />
                    <ToolDetailKeyValue
                        label="Confirmation"
                        value={
                            formatDetailStatus(
                                getStringValue(
                                    assignment,
                                    'confirmation_status',
                                ),
                            ) ?? 'Not returned'
                        }
                    />
                </div>
            </ToolTopicSection>
            <ToolTopicSection title="Requirements">
                <div className="space-y-2">
                    <ToolDetailKeyValue
                        label="Role"
                        value={
                            formatDetailStatus(
                                getStringValue(requirements, 'role'),
                            ) ?? 'Not provided'
                        }
                    />
                    <DetailChipList
                        values={getStringArray(requirements['skills'])}
                    />
                </div>
            </ToolTopicSection>
            {relatedCallouts.length > 0 ? (
                <ToolTopicSection title="Related outreach">
                    <div className="space-y-1">
                        {relatedCallouts.map((callout, index) => (
                            <p
                                key={`related-callout-${index}`}
                                className="text-foreground text-xs"
                            >
                                {joinPresent([
                                    formatDetailStatus(
                                        getStringValue(callout, 'status'),
                                    ),
                                    callout['use_callout_fill'] === true
                                        ? 'fillable'
                                        : null,
                                ]) ?? 'Outreach'}
                            </p>
                        ))}
                    </div>
                </ToolTopicSection>
            ) : null}
            {getStringValue(notes, 'for_caregiver') ||
            getStringValue(notes, 'internal') ? (
                <ToolTopicSection title="Notes">
                    <div className="space-y-1.5">
                        {getStringValue(notes, 'for_caregiver') ? (
                            <p className="text-foreground text-xs">
                                {getStringValue(notes, 'for_caregiver')}
                            </p>
                        ) : null}
                        {getStringValue(notes, 'internal') ? (
                            <p className="text-muted-foreground text-xs">
                                {getStringValue(notes, 'internal')}
                            </p>
                        ) : null}
                    </div>
                </ToolTopicSection>
            ) : null}
        </div>
    );
}

export function getOutreachShifts(
    data: Record<string, unknown>,
): Array<Record<string, unknown>> {
    if (Array.isArray(data['shifts'])) {
        return data['shifts'].filter(isRecord);
    }
    return isRecord(data['shift']) ? [data['shift']] : [];
}

export function formatOutreachShift(
    shift: Record<string, unknown>,
    formatName: (name: string | null | undefined) => string,
): string {
    const patientName =
        formatName(
            getStringValue(shift, 'patient_name') ??
                getStringValue(shift, 'client_name'),
        ) || 'Shift';
    const date = getStringValue(shift, 'shift_day');
    const start = getStringValue(shift, 'start_time');
    const end = getStringValue(shift, 'end_time');
    const time = start && end ? `${start} - ${end}` : start;
    return joinPresent([patientName, date, time]) ?? patientName;
}

export function OutreachDetailsCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const { formatName } = useFormatPersonName();
    const shifts = getOutreachShifts(data);
    const shiftGroups = Array.isArray(data['shift_groups'])
        ? data['shift_groups'].filter(isRecord)
        : [];
    const originalCaregivers = Array.isArray(data['original_caregivers'])
        ? data['original_caregivers'].filter(isRecord)
        : [];
    const suggestedCaregivers = Array.isArray(data['suggested_caregivers'])
        ? data['suggested_caregivers'].filter(isRecord)
        : [];
    const contactAttemptSummary = isRecord(data['contact_attempt_summary'])
        ? data['contact_attempt_summary']
        : null;
    const status =
        formatDetailStatus(getStringValue(data, 'status')) ??
        formatDetailStatus(getStringValue(data, 'outreach_status'));
    const outreachType = formatDetailStatus(
        getStringValue(data, 'outreach_type'),
    );
    const agentState = formatDetailStatus(
        getStringValue(data, 'agent_run_status') ??
            getStringValue(data, 'outreach_agent_state'),
    );

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title={outreachType ?? 'Outreach'}
                subtitle={
                    getStringValue(data, 'organization_timezone')
                        ? `Timezone: ${getStringValue(
                              data,
                              'organization_timezone',
                          )}`
                        : null
                }
                badges={[
                    ...(status
                        ? [{ label: status, tone: 'blue' as const }]
                        : []),
                    ...(agentState
                        ? [{ label: agentState, tone: 'neutral' as const }]
                        : []),
                ]}
            />
            {getStringValue(data, 'callout_reason') ||
            getStringValue(data, 'instructions') ? (
                <ToolTopicSection title="Context">
                    <div className="space-y-1.5">
                        {getStringValue(data, 'callout_reason') ? (
                            <p className="text-foreground text-xs">
                                {getStringValue(data, 'callout_reason')}
                            </p>
                        ) : null}
                        {getStringValue(data, 'instructions') ? (
                            <p className="text-muted-foreground text-xs">
                                {getStringValue(data, 'instructions')}
                            </p>
                        ) : null}
                    </div>
                </ToolTopicSection>
            ) : null}
            <ToolTopicSection title="Shifts">
                {shifts.length > 0 ? (
                    <div className="space-y-2">
                        {shifts.slice(0, 4).map((shift, index) => (
                            <ShiftInfoCard
                                key={
                                    getStringValue(shift, 'shift_id') ??
                                    `outreach-shift-${index}`
                                }
                                shift={shift}
                                linkToShift={true}
                            />
                        ))}
                        {shifts.length > 4 ? (
                            <p className="text-muted-foreground text-[11px]">
                                +{shifts.length - 4} more shifts
                            </p>
                        ) : null}
                    </div>
                ) : (
                    <p className="text-muted-foreground text-xs italic">
                        No shifts returned
                    </p>
                )}
            </ToolTopicSection>
            {shiftGroups.length > 0 ? (
                <ToolTopicSection title="Shift groups">
                    <div className="space-y-1">
                        {shiftGroups.map((group, index) => (
                            <p
                                key={`shift-group-${index}`}
                                className="text-foreground text-xs"
                            >
                                {getStringValue(group, 'group_mode')
                                    ? formatToolFieldLabel(
                                          getStringValue(group, 'group_mode')!,
                                      )
                                    : 'Group'}{' '}
                                ·{' '}
                                {Array.isArray(group['shift_ids'])
                                    ? group['shift_ids'].length
                                    : 0}{' '}
                                shifts
                            </p>
                        ))}
                    </div>
                </ToolTopicSection>
            ) : null}
            {originalCaregivers.length > 0 ? (
                <ToolTopicSection title="Original caregivers">
                    <div className="space-y-1">
                        {originalCaregivers.map((caregiver, index) => (
                            <p
                                key={`original-caregiver-${index}`}
                                className="text-foreground text-xs"
                            >
                                {formatName(
                                    getStringValue(caregiver, 'caregiver_name'),
                                ) || 'Caregiver'}
                            </p>
                        ))}
                    </div>
                </ToolTopicSection>
            ) : null}
            {suggestedCaregivers.length > 0 ? (
                <ToolTopicSection title="Suggested caregivers">
                    <div className="space-y-1">
                        {suggestedCaregivers.slice(0, 4).map((suggestion) => (
                            <p
                                key={
                                    getStringValue(
                                        suggestion,
                                        'caregiver_id',
                                    ) ??
                                    getStringValue(
                                        suggestion,
                                        'caregiver_name',
                                    ) ??
                                    'suggestion'
                                }
                                className="text-foreground text-xs"
                            >
                                {joinPresent([
                                    getNumberValue(suggestion, 'rank') !== null
                                        ? `#${getNumberValue(
                                              suggestion,
                                              'rank',
                                          )}`
                                        : null,
                                    formatName(
                                        getStringValue(
                                            suggestion,
                                            'caregiver_name',
                                        ),
                                    ),
                                    getNumberValue(suggestion, 'distance') !==
                                    null
                                        ? `${getNumberValue(
                                              suggestion,
                                              'distance',
                                          )?.toFixed(1)} mi`
                                        : null,
                                ]) ?? 'Caregiver'}
                            </p>
                        ))}
                    </div>
                </ToolTopicSection>
            ) : null}
            {contactAttemptSummary ||
            typeof data['contact_attempts'] === 'number' ? (
                <ToolTopicSection title="Contact attempts">
                    <p className="text-foreground text-xs">
                        {getNumberValue(contactAttemptSummary, 'count') ??
                            getNumberValue(data, 'contact_attempts') ??
                            0}{' '}
                        attempts returned
                    </p>
                </ToolTopicSection>
            ) : null}
        </div>
    );
}
